import { useEffect, useState } from "react";
import { AxiosInstance, isCancel } from "axios";
import { URL } from "../libraries/http/url";
import { Predicates } from "../libraries/predicates/predicates";
import { Pageable, PageableDTO, PageableQuery } from "../types/pageable";
import useAxios from "../utils/useAxios";
import {
  IColor,
  IDecorationTech,
  IGlobalProduct,
  ILicensedProperty,
  ILicensor,
  IMold,
} from "../types/data.interface";
import { useMetaArtworkSearchStore } from "../context/MetaArtworkSearchContext";
import { MasterArtworkSearchActions } from "../states/meta-artwork/search";

type ValueOrNull<T> = T | null;

type ArtworkSearch = {
  queryString?: string;
  search?: string;
} & Partial<PageableQuery>;

export type ArtworkDTO = {
  name: string;
  code: string;
  licensor: ValueOrNull<ILicensor>;
  licensed_property: ValueOrNull<ILicensedProperty>;
  launch_date: string;
  global_product: ValueOrNull<IGlobalProduct>;
  decoration_technique: ValueOrNull<IDecorationTech>;
  color: ValueOrNull<IColor>;
  molds: ValueOrNull<IMold>;
  artwork_name: string;
  //collection: ValueOrNull<ICollec>;
};

export type ArtworkSearchResponse = PageableDTO<ArtworkDTO[]> | ArtworkDTO[];

class ArtworkMapper {
  static pageableToModel(
    dto: ArtworkSearchResponse,
    offset?: number,
  ): Pageable<ArtworkDTO> {
    return {
      total: Predicates.isPageable(dto) ? dto.count : 0,
      offset: offset ?? 0,
      data: Predicates.isPageable(dto) ? dto.results : dto,
    };
  }
}

const fetchArtworkBySearch = async ({
  axios,
  limit,
  offset,
  queryString,
  search,
  signal,
}: ArtworkSearch & { axios: AxiosInstance; signal: AbortSignal }) => {
  const searchParams = URL.createSearchParams(
    {
      limit,
      offset,
      search,
    },
    queryString,
  );

  const response = await axios.get<ArtworkSearchResponse>(
    `/artwork-search/?${searchParams.toString()}`,
    { signal },
  );

  return ArtworkMapper.pageableToModel(response.data, offset);
};

export default function useArtworkSearch({
  limit,
  offset,
  queryString,
  search,
}: ArtworkSearch) {
  const [data, setData] = useState<{
    isLoading: boolean;
    data?: Pageable<ArtworkDTO>;
  }>({ isLoading: false });

  const { state, dispatch } = useMetaArtworkSearchStore();

  const axios = useAxios() as AxiosInstance;

  const controller = new AbortController();
  const getData = async () => {
    try {
      setData({ isLoading: true });
      const response = await fetchArtworkBySearch({
        axios,
        limit,
        offset,
        queryString,
        search,
        signal: controller.signal,
      });

      setData({ isLoading: false, data: response });
    } catch (err) {
      if (!isCancel(err)) {
        console.error("Fetch error:", err);
        setData({ isLoading: false });
      }
    }
  };

  useEffect(() => {
    if (
      Predicates.isNotNullAndNotUndefinedAndNotEmpty(queryString) ||
      Predicates.isNotNullAndNotUndefinedAndNotEmpty(search)
    ) {
      getData();
    }

    return () => {
      controller.abort();
    };
  }, [limit, offset, queryString, search]);

  useEffect(() => {
    if(state){
    if (state.artworkLevel === "collection") {
      dispatch(
        MasterArtworkSearchActions.setResponseDataCollection(data.data),
      );

      dispatch(MasterArtworkSearchActions.setLimitCollection(limit ?? 50));
    } else if (state.artworkLevel === "meta-artwork") {
      dispatch(
        MasterArtworkSearchActions.setResponseDataMetaArtwork(data.data),
      );

      dispatch(MasterArtworkSearchActions.setLimitMetaArtwork(limit ?? 50));
      dispatch(
        MasterArtworkSearchActions.setAppliedFiltersCollection(
          queryString ?? null,
        ),
      );
      dispatch(
        MasterArtworkSearchActions.setSearchCollection(search ?? null),
      );
    } else if (state.artworkLevel === "printing-technique") {
      dispatch(
        MasterArtworkSearchActions.setResponseDataPrintingTechnique(
          data.data,
        ),
      );
      dispatch(
        MasterArtworkSearchActions.setAppliedFiltersMetaArtwork(
          queryString ?? null,
        ),
      );
      dispatch(
        MasterArtworkSearchActions.setSearchMetaArtwork(search ?? null),
      );
      dispatch(
        MasterArtworkSearchActions.setLimitPrintingTechnique(limit ?? 50),
      );
      dispatch(
        MasterArtworkSearchActions.setAppliedFiltersPrintingTechnique(
          queryString ?? null,
        ),
      );
      dispatch(
        MasterArtworkSearchActions.setSearchPrintingTechnique(search ?? null),
      );
    }
    }
  },[data])

  return data;
}
